import {
  addDaysIso,
  isDemandLikeInvestmentType,
  parseIsoDate,
  toNumberOrNull,
  trimToEmpty,
} from "./portfolioReportPositions";
import {
  dayCountFraction,
  dayCountCouponPeriodFraction,
  freqToPeriodsPerYear,
} from "lib/dayCount";
import { normalizeCouponRate } from "pages/FundPlanner/lib/portfolioAnalytics";
import { buildSlgsNoteCashflows } from "pages/FundPlanner/lib/slgsNoteSchedule";

function formatMonthKey(dateIso) {
  return `${dateIso.slice(0, 7)}-01`;
}

function formatQuarterKey(dateIso) {
  const year = dateIso.slice(0, 4);
  const month = Number(dateIso.slice(5, 7));
  const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
  return `${year}-${String(quarterStartMonth).padStart(2, "0")}-01`;
}

function formatYearKey(dateIso) {
  return `${dateIso.slice(0, 4)}-01-01`;
}

export function getCashFlowBucketKey(dateIso, bucketMode = "monthly") {
  if (bucketMode === "date") return dateIso;
  if (bucketMode === "quarterly") return formatQuarterKey(dateIso);
  if (bucketMode === "annual") return formatYearKey(dateIso);
  return formatMonthKey(dateIso);
}

export function formatCashFlowBucketLabel(dateIso, bucketMode = "monthly") {
  const date = parseIsoDate(dateIso);
  if (!date) return dateIso || "";

  if (bucketMode === "date") {
    return date.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "2-digit",
    });
  }

  if (bucketMode === "quarterly") {
    const quarter = Math.floor(date.getMonth() / 3) + 1;
    return `Q${quarter} ${date.getFullYear()}`;
  }

  if (bucketMode === "annual") {
    return String(date.getFullYear());
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    year: "2-digit",
  });
}

export function addYearsIso(dateIso, years) {
  const date = parseIsoDate(dateIso);
  if (!date) return "";
  date.setFullYear(date.getFullYear() + years);
  return date.toISOString().slice(0, 10);
}

function addMonthsIso(dateIso, months) {
  const date = parseIsoDate(dateIso);
  if (!date) return "";
  const day = date.getDate();
  date.setMonth(date.getMonth() + months, 1);
  const maxDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(day, maxDay));
  return date.toISOString().slice(0, 10);
}

function isDateInRange(dateIso, startDate, endDate) {
  return dateIso > startDate && dateIso <= endDate;
}

function getPositionAmount(row) {
  return toNumberOrNull(row?.current_amount ?? row?.amount ?? row?.par_amount);
}

function isPositionRow(row) {
  return !row?.row_type || row.row_type === "position";
}

function getScheduledMaturityDate(row) {
  return trimToEmpty(row?.maturity_date || row?.effective_maturity_date);
}

function getCashFlowCategory(row) {
  return isDemandLikeInvestmentType(row?.investment_type) ? "demand" : "scheduled";
}

function normalizeText(value) {
  return trimToEmpty(value).toLowerCase();
}

function isSlgsTimeDeposit(row) {
  return normalizeText(row?.investment_type) === "slgs time deposit";
}

function getSecurityDescriptor(row) {
  return [
    row?.coupon_type,
    row?.security_type,
    row?.investment_type,
  ]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ");
}

function isZeroCouponLike(row) {
  const text = getSecurityDescriptor(row);
  const coupon = normalizeCouponRate(row?.coupon);
  return (
    text.includes("zero") ||
    text.includes("discount") ||
    text.includes("bill") ||
    normalizeText(row?.coupon_type) === "zero" ||
    coupon === 0
  );
}

function isVariableCouponLike(row) {
  const type = normalizeText(row?.coupon_type);
  return (
    type.includes("adjust") ||
    type.includes("float") ||
    type.includes("variable") ||
    type.includes("index")
  );
}

export function isFixedCouponLike(row) {
  const coupon = normalizeCouponRate(row?.coupon);
  if (coupon == null || coupon <= 0) return false;
  if (isZeroCouponLike(row) || isVariableCouponLike(row)) return false;

  const type = normalizeText(row?.coupon_type);
  return (
    !type ||
    type.includes("fixed") ||
    type.includes("coupon") ||
    type.includes("interest")
  );
}

function isTreasuryCouponSecurity(row) {
  const category = normalizeText(row?.security_category);
  if (category === "treasury_note_bond") return true;
  if (isZeroCouponLike(row)) return false;

  return getSecurityDescriptor(row).includes("treasury");
}

function getCouponDayCountBasis(row) {
  if (isTreasuryCouponSecurity(row)) return "act/act-ust";
  return trimToEmpty(row?.day_count_basis) || "30/360";
}

function getCouponAccrualStartDate(row, previousCouponDate, couponDate) {
  const datedDate = trimToEmpty(row?.dated_date);
  const firstInterestPaymentDate = trimToEmpty(
    row?.first_interest_payment_date
  );
  if (firstInterestPaymentDate === couponDate) {
    return datedDate || trimToEmpty(row?.settlement_date) || previousCouponDate;
  }

  if (
    datedDate &&
    datedDate > previousCouponDate &&
    datedDate < couponDate
  ) {
    return datedDate;
  }

  return previousCouponDate;
}

function getPaymentFrequency(row) {
  const raw = trimToEmpty(row?.payment_frequency) || "semiannual";
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric > 0) return numeric;

  const normalized = raw.toLowerCase();
  if (
    normalized.includes("maturity") ||
    normalized.includes("bullet") ||
    normalized.includes("none") ||
    normalized === "zero"
  ) {
    return null;
  }

  if (
    !normalized.startsWith("month") &&
    !normalized.startsWith("quarter") &&
    !normalized.startsWith("annual") &&
    !normalized.startsWith("semi")
  ) {
    return null;
  }

  const periods = freqToPeriodsPerYear(raw);
  return Number.isFinite(periods) && periods > 0 ? periods : null;
}

export function getLatestScheduledMaturityDate(rows = [], asOfDate = "") {
  let latest = "";

  for (const row of rows) {
    if (!isPositionRow(row)) continue;
    if (getCashFlowCategory(row) !== "scheduled") continue;

    const maturityDate = getScheduledMaturityDate(row);
    if (!maturityDate || (asOfDate && maturityDate <= asOfDate)) continue;
    if (!latest || maturityDate > latest) latest = maturityDate;
  }

  return latest;
}

export function buildCouponDates(row, startDate, endDate) {
  const maturityDate = trimToEmpty(row?.maturity_date || row?.effective_maturity_date);
  const firstInterestPaymentDate = trimToEmpty(
    row?.first_interest_payment_date
  );
  const coupon = normalizeCouponRate(row?.coupon);
  const frequency = getPaymentFrequency(row);
  if (!maturityDate || coupon == null || coupon <= 0 || !frequency) return [];

  const dates = [];
  const months = 12 / frequency;

  if (firstInterestPaymentDate) {
    for (
      let i = 0, payDate = firstInterestPaymentDate;
      i < 600 && payDate && payDate <= maturityDate;
      i += 1, payDate = addMonthsIso(payDate, months)
    ) {
      if (isDateInRange(payDate, startDate, endDate)) dates.push(payDate);
    }

    if (
      maturityDate >= firstInterestPaymentDate &&
      isDateInRange(maturityDate, startDate, endDate) &&
      !dates.includes(maturityDate)
    ) {
      dates.push(maturityDate);
    }
  } else {
    let payDate = maturityDate;
    for (let i = 0; i < 600 && payDate > startDate; i += 1) {
      if (isDateInRange(payDate, startDate, endDate)) dates.push(payDate);
      payDate = addMonthsIso(payDate, -months);
      if (!payDate) break;
    }
  }

  return Array.from(new Set(dates)).sort();
}

function calculateCouponInterest(row, principal, couponDate) {
  const coupon = normalizeCouponRate(row?.coupon) ?? 0;
  const frequency = getPaymentFrequency(row);
  if (!frequency) return 0;

  const previousCouponDate = addMonthsIso(couponDate, -(12 / frequency));
  const accrualStartDate = getCouponAccrualStartDate(
    row,
    previousCouponDate,
    couponDate
  );
  const dayCountBasis = getCouponDayCountBasis(row);
  const accrual = dayCountCouponPeriodFraction({
    start: accrualStartDate,
    end: couponDate,
    nextCoupon: couponDate,
    method: dayCountBasis,
    frequency,
  });

  return principal * coupon * accrual;
}

function buildSlgsTimeDepositInterestFlows(row, principal, startDate, endDate) {
  const settlementDate = trimToEmpty(
    row?.settlement_date || row?.purchase_date
  );
  const maturityDate = getScheduledMaturityDate(row);
  const coupon = normalizeCouponRate(row?.coupon);
  const settlement = parseIsoDate(settlementDate);
  const maturity = parseIsoDate(maturityDate);

  if (!settlement || !maturity || coupon == null || coupon <= 0) return [];

  const certificateCutoff = new Date(settlement.getTime());
  certificateCutoff.setFullYear(certificateCutoff.getFullYear() + 1);

  // SLGS certificates (one year or less) pay simple ACT/ACT interest at maturity.
  if (maturity <= certificateCutoff) {
    if (!isDateInRange(maturityDate, startDate, endDate)) return [];
    return [
      {
        date: maturityDate,
        amount:
          principal *
          coupon *
          dayCountFraction(settlementDate, maturityDate, "act/act"),
      },
    ];
  }

  // Longer SLGS Time Deposits are notes with Treasury's semiannual schedule.
  const { regular } = buildSlgsNoteCashflows({
    settlement: settlementDate,
    maturity: maturityDate,
    principal,
    ratePct: coupon * 100,
    descriptionBase: trimToEmpty(row?.cusip_or_id) || "SLGS",
  });

  return regular
    .filter((flow) => isDateInRange(flow.date, startDate, endDate))
    .map((flow) => ({
      date: flow.date,
      amount: flow.amount - (flow.includesPrincipal ? principal : 0),
    }));
}

function getCouponProjectionNote(row) {
  const id = row?.cusip_or_id || row?.display_name || "Position";

  if (isZeroCouponLike(row)) return "";
  if (isVariableCouponLike(row)) {
    return `${id} has a variable coupon type; interest cash flows are not projected.`;
  }

  const coupon = normalizeCouponRate(row?.coupon);
  if (coupon == null || coupon <= 0) return "";

  if (!isFixedCouponLike(row)) {
    return `${id} has unsupported coupon type "${row?.coupon_type || "Unknown"}"; interest cash flows are not projected.`;
  }

  if (!getPaymentFrequency(row)) {
    return `${id} has unsupported payment frequency "${row?.payment_frequency || "Unknown"}"; interest cash flows are not projected.`;
  }

  return "";
}

function addFlow(flowMap, detailRows, flow) {
  const existing = flowMap.get(flow.bucketKey) || {
    bucketKey: flow.bucketKey,
    label: flow.label,
    principal: 0,
    interest: 0,
    demandLiquidity: 0,
    scheduledTotal: 0,
    totalIncludingLiquidity: 0,
  };

  existing.principal += flow.principal || 0;
  existing.interest += flow.interest || 0;
  existing.demandLiquidity += flow.demandLiquidity || 0;
  existing.scheduledTotal = existing.principal + existing.interest;
  existing.totalIncludingLiquidity =
    existing.scheduledTotal + existing.demandLiquidity;

  flowMap.set(flow.bucketKey, existing);
  detailRows.push(flow);
}

function fillEmptyCashFlowBuckets(flowMap, bucketMode) {
  if (bucketMode === "date" || flowMap.size < 2) return;

  const populatedKeys = Array.from(flowMap.keys()).sort((a, b) => a.localeCompare(b));
  const firstKey = populatedKeys[0];
  const lastKey = populatedKeys[populatedKeys.length - 1];
  const monthsPerBucket = bucketMode === "annual" ? 12 : bucketMode === "quarterly" ? 3 : 1;

  for (
    let bucketKey = firstKey;
    bucketKey && bucketKey <= lastKey;
    bucketKey = addMonthsIso(bucketKey, monthsPerBucket)
  ) {
    if (!flowMap.has(bucketKey)) {
      flowMap.set(bucketKey, {
        bucketKey,
        label: formatCashFlowBucketLabel(bucketKey, bucketMode),
        principal: 0,
        interest: 0,
        demandLiquidity: 0,
        scheduledTotal: 0,
        totalIncludingLiquidity: 0,
      });
    }
  }
}

export function buildPortfolioCashFlows({
  rows = [],
  asOfDate,
  projectionEndDate,
  bucketMode = "monthly",
  includeScheduledCashFlows = true,
  includeDemandLiquidity = false,
}) {
  const startDate = trimToEmpty(asOfDate);
  const endDate = trimToEmpty(projectionEndDate);
  const flowMap = new Map();
  const detailRows = [];
  const notes = [];

  if (!startDate || !endDate || endDate <= startDate) {
    return {
      chartRows: [],
      detailRows: [],
      notes: ["Select a projection end date after the as-of date."],
      totals: {
        principal: 0,
        interest: 0,
        demandLiquidity: 0,
        scheduledTotal: 0,
        totalIncludingLiquidity: 0,
      },
    };
  }

  const demandBucketKey = getCashFlowBucketKey(addDaysIso(startDate, 1), bucketMode);

  for (const row of rows) {
    if (!isPositionRow(row)) continue;

    const amount = getPositionAmount(row);
    if (amount == null || amount <= 0) continue;

    const cashFlowCategory = getCashFlowCategory(row);
    if (cashFlowCategory === "scheduled" && !includeScheduledCashFlows) continue;
    if (cashFlowCategory === "demand" && !includeDemandLiquidity) continue;

    if (cashFlowCategory === "demand") {
      addFlow(flowMap, detailRows, {
        id: `${row.position_id || row.id}:demand`,
        bucketKey: demandBucketKey,
        label: formatCashFlowBucketLabel(demandBucketKey, bucketMode),
        date: startDate,
        cashFlowCategory,
        type: "Demand Liquidity",
        investmentType: row.investment_type,
        fundName: row.fund_name,
        issuerName: row.issuer_name,
        cusipOrId: row.cusip_or_id,
        principal: 0,
        interest: 0,
        demandLiquidity: amount,
        amount,
      });
      continue;
    }

    const maturityDate = getScheduledMaturityDate(row);
    if (!maturityDate) {
      notes.push(`${row.cusip_or_id || row.display_name || "Position"} is missing a maturity date.`);
      continue;
    }

    if (isDateInRange(maturityDate, startDate, endDate)) {
      const bucketKey = getCashFlowBucketKey(maturityDate, bucketMode);
      addFlow(flowMap, detailRows, {
        id: `${row.position_id || row.id}:principal`,
        bucketKey,
        label: formatCashFlowBucketLabel(bucketKey, bucketMode),
        date: maturityDate,
        cashFlowCategory,
        type: "Principal",
        investmentType: row.investment_type,
        fundName: row.fund_name,
        issuerName: row.issuer_name,
        cusipOrId: row.cusip_or_id,
        principal: amount,
        interest: 0,
        demandLiquidity: 0,
        amount,
      });
    }

    if (isSlgsTimeDeposit(row)) {
      for (const slgsFlow of buildSlgsTimeDepositInterestFlows(
        row,
        amount,
        startDate,
        endDate
      )) {
        const bucketKey = getCashFlowBucketKey(slgsFlow.date, bucketMode);
        addFlow(flowMap, detailRows, {
          id: `${row.position_id || row.id}:interest:${slgsFlow.date}`,
          bucketKey,
          label: formatCashFlowBucketLabel(bucketKey, bucketMode),
          date: slgsFlow.date,
          cashFlowCategory,
          type: "Interest",
          investmentType: row.investment_type,
          fundName: row.fund_name,
          issuerName: row.issuer_name,
          cusipOrId: row.cusip_or_id,
          principal: 0,
          interest: slgsFlow.amount,
          demandLiquidity: 0,
          amount: slgsFlow.amount,
        });
      }
    } else {
      const couponProjectionNote = getCouponProjectionNote(row);
      if (couponProjectionNote) {
        notes.push(couponProjectionNote);
      } else if (isFixedCouponLike(row)) {
        for (const couponDate of buildCouponDates(row, startDate, endDate)) {
          const interest = calculateCouponInterest(row, amount, couponDate);
          const bucketKey = getCashFlowBucketKey(couponDate, bucketMode);

          addFlow(flowMap, detailRows, {
            id: `${row.position_id || row.id}:interest:${couponDate}`,
            bucketKey,
            label: formatCashFlowBucketLabel(bucketKey, bucketMode),
            date: couponDate,
            cashFlowCategory,
            type: "Interest",
            investmentType: row.investment_type,
            fundName: row.fund_name,
            issuerName: row.issuer_name,
            cusipOrId: row.cusip_or_id,
            principal: 0,
            interest,
            demandLiquidity: 0,
            amount: interest,
          });
        }
      }
    }
  }

  fillEmptyCashFlowBuckets(flowMap, bucketMode);

  const chartRows = Array.from(flowMap.values())
    .sort((a, b) => a.bucketKey.localeCompare(b.bucketKey))
    .map((row) => ({
      ...row,
      label: formatCashFlowBucketLabel(row.bucketKey, bucketMode),
      scheduledTotal: row.principal + row.interest,
      totalIncludingLiquidity: row.principal + row.interest + row.demandLiquidity,
    }));

  const totals = chartRows.reduce(
    (acc, row) => {
      acc.principal += row.principal;
      acc.interest += row.interest;
      acc.demandLiquidity += row.demandLiquidity;
      acc.scheduledTotal += row.scheduledTotal;
      acc.totalIncludingLiquidity += row.totalIncludingLiquidity;
      return acc;
    },
    {
      principal: 0,
      interest: 0,
      demandLiquidity: 0,
      scheduledTotal: 0,
      totalIncludingLiquidity: 0,
    }
  );

  return {
    chartRows,
    detailRows: detailRows.sort((a, b) => a.date.localeCompare(b.date)),
    notes: Array.from(new Set(notes)),
    totals,
  };
}
